import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Reply, Send, Check } from "lucide-react";

const TEMPLATES = [
  { id: "bring_in", label: "Bring it in", text: (f) => `Hi ${f}, thanks for reaching out. Can you bring the car in Thursday at 9am and we'll take a look? If that doesn't suit, just reply with a time that works. — Tassie Automotive` },
  { id: "book_online", label: "Book online", text: (f) => `Hi ${f}, easiest is to grab a time that suits you here: https://tassieautomotive.com.au/book — Tassie Automotive` },
  { id: "call_us", label: "Call us", text: (f) => `Hi ${f}, could you give us a quick call on (03) 6288 7861 so we can talk it through? — Tassie Automotive` },
  { id: "quote", label: "Quote coming", text: (f) => `Hi ${f}, thanks for the details — we'll put together a quote and get back to you shortly. — Tassie Automotive` },
];

const isAuMobile = (p) => /^04\d{8}$/.test((p || "").replace(/\D/g, ""));

export function MessageCard({ msg, onReplied }) {
  const [open, setOpen] = useState(false);
  const [body, setBody] = useState("");
  const [alsoSms, setAlsoSms] = useState(isAuMobile(msg.phone));
  const [sending, setSending] = useState(false);
  const first = (msg.name || "").split(" ")[0] || "there";

  const send = async () => {
    if (!body.trim()) return;
    setSending(true);
    try {
      const r = await api.post(`/admin/contact/${msg.id}/reply`, { body: body.trim(), send_sms: alsoSms });
      const e = r.data.email, s = r.data.sms;
      const bits = [`email ${e?.sent ? "✓" : "✗"}`];
      if (alsoSms) bits.push(`SMS ${s?.sent ? "✓" : "✗"}`);
      toast.success(`Reply sent — ${bits.join(", ")}`);
      setOpen(false);
      setBody("");
      onReplied?.(r.data.message);
    } catch {
      toast.error("Could not send reply");
    } finally {
      setSending(false);
    }
  };

  return (
    <article data-testid={`admin-msg-${msg.id}`} className="bg-zinc-950 p-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-display uppercase text-lg text-white tracking-tight">{msg.name}</div>
          <div className="text-xs text-zinc-500">{msg.email}{msg.phone ? ` · ${msg.phone}` : ""}</div>
        </div>
        <div className="text-right">
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{(msg.created_at || "").slice(0, 10)}</div>
          {msg.replied_at && (
            <span data-testid={`msg-replied-${msg.id}`} className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider bg-emerald-500/10 text-emerald-400 border border-emerald-500/30">
              <Check className="h-3 w-3" /> Replied
            </span>
          )}
        </div>
      </div>
      <p className="mt-3 text-sm text-zinc-300 leading-relaxed">{msg.message}</p>

      {!open ? (
        <button
          data-testid={`msg-reply-btn-${msg.id}`}
          onClick={() => setOpen(true)}
          className="mt-4 inline-flex items-center gap-2 px-4 py-2 text-xs font-mono uppercase tracking-widest border border-zinc-700 text-zinc-300 hover:border-brand-red hover:text-brand-red transition-colors"
        >
          <Reply className="h-3.5 w-3.5" /> {msg.replied_at ? "Reply again" : "Reply"}
        </button>
      ) : (
        <div className="mt-4 space-y-3" data-testid={`msg-reply-panel-${msg.id}`}>
          <div className="flex flex-wrap gap-2">
            {TEMPLATES.map((t) => (
              <button
                key={t.id}
                data-testid={`msg-template-${t.id}-${msg.id}`}
                onClick={() => setBody(t.text(first))}
                className="px-3 py-1.5 text-[11px] font-mono uppercase tracking-wider border border-zinc-800 text-zinc-400 hover:border-brand-red hover:text-brand-red transition-colors"
              >
                {t.label}
              </button>
            ))}
          </div>
          <textarea
            data-testid={`msg-reply-body-${msg.id}`}
            rows={4}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={`Hi ${first}, …  (tap a template above or write your own)`}
            className="w-full bg-black border border-zinc-800 p-3 text-sm text-zinc-200 focus:border-brand-red outline-none resize-none"
          />
          <div className="flex items-center justify-between gap-3 flex-wrap">
            {isAuMobile(msg.phone) ? (
              <label className="inline-flex items-center gap-2 text-xs text-zinc-400 cursor-pointer">
                <input
                  data-testid={`msg-reply-sms-toggle-${msg.id}`}
                  type="checkbox"
                  checked={alsoSms}
                  onChange={(e) => setAlsoSms(e.target.checked)}
                  className="accent-current"
                />
                Also text {msg.phone}
              </label>
            ) : <span className="text-xs text-zinc-600">Reply goes by email{msg.phone ? " (phone isn't an 04 mobile)" : ""}</span>}
            <div className="flex gap-2">
              <button
                data-testid={`msg-reply-cancel-${msg.id}`}
                onClick={() => setOpen(false)}
                className="px-4 py-2 text-xs font-mono uppercase tracking-widest text-zinc-500 hover:text-white"
              >
                Cancel
              </button>
              <button
                data-testid={`msg-reply-send-${msg.id}`}
                onClick={send}
                disabled={sending || !body.trim()}
                className="inline-flex items-center gap-2 px-5 py-2 bg-brand-red text-white text-xs font-mono uppercase tracking-widest hover:bg-[#B91C2B] disabled:opacity-50"
              >
                <Send className="h-3.5 w-3.5" /> {sending ? "Sending…" : "Send reply"}
              </button>
            </div>
          </div>
        </div>
      )}
    </article>
  );
}
